import { produce } from 'immer';
import update from 'immutability-helper';
import { IListItem, TravelType } from './type';
import { onDisablePermissionSwitch } from './utils';
import { IDialogAction, IDialogStates, DIALOG_ACTION_TYPE, IUplevelDepartment } from './DialogContext';

export function DialogReducer(state: IDialogStates, action: IDialogAction): IDialogStates {
    let newState = state;
    switch (action.type) {
        case DIALOG_ACTION_TYPE.CLEAN:
            return produce(state, (draft) => {
                draft.dataList = [];
                draft.selectedList = [];
                draft.selectedMemberMap = new Map();
                draft.selectedDepartmentMap = new Map();
            });
        case DIALOG_ACTION_TYPE.CLEAN_SELECTED_LIST:
            return produce(state, (draft) => {
                draft.selectedList = [];
            });
        case DIALOG_ACTION_TYPE.CLEAN_DATA_LIST:
            return produce(state, (draft) => {
                draft.dataList = [];
            });
        case DIALOG_ACTION_TYPE.SET_DATALIST:
            return setDataList(state, action);
        case DIALOG_ACTION_TYPE.SET_SELECTED_LIST:
            return setSelectedList(state, action);
        case DIALOG_ACTION_TYPE.SET_MEMBER_SELECTED_MAP:
            return setMemberSelectedMap(state, action);
        case DIALOG_ACTION_TYPE.SET_DEPARTMENT_SELECTED_MAP:
            return setDepartmentSelectedMap(state, action);

        case DIALOG_ACTION_TYPE.SET_ALL_LIST_CHECKED:
            return setAllListChecked(state, action);
        case DIALOG_ACTION_TYPE.UPDATE_DATA_SELECTED:
            return updateDataListAndSelectedList(state, action);
        case DIALOG_ACTION_TYPE.UPDATE_SELECTED:
            return updateSelectedItem(state, action);
        case DIALOG_ACTION_TYPE.SET_DATASUG:
            return setDataSug(state, action);
        case DIALOG_ACTION_TYPE.SET_CACHE:
            const enterpriseName = action.payload.enterpriseName || state.enterpriseName;
            const departmentCache = action.payload.departmentCache || state.departmentCache;
            newState = update(state, {
                departmentCache: {
                    $set: departmentCache,
                },
                enterpriseName: {
                    $set: enterpriseName,
                },
            });
            return newState;
        case DIALOG_ACTION_TYPE.SET_IS_SEARCH_MODE:
            return setIsSearchMode(state, action);
        case DIALOG_ACTION_TYPE.CLEAN_SELECTED:
            return cleanSelectedList(state, action);
        case DIALOG_ACTION_TYPE.SET_UPLEVEL_DEPARTMENT:
            const uplevelDepartmentList = action.payload.uplevelDepartmentList || [];
            const uplevelIdMap = action.payload.uplevelIdMap || {};
            const uplevelDepartment: IUplevelDepartment = {};
            uplevelDepartmentList.forEach((i: IListItem) => {
                if (!uplevelDepartment[i.id]) {
                    uplevelDepartment[i.id] = i;
                }
            });
            newState = update(state, {
                uplevelDepartment: {
                    $set: uplevelDepartment,
                },
                uplevelIdMap: {
                    $set: uplevelIdMap,
                },
            });
            return newState;
        default:
            newState = state;
    }
    return newState;
}

function setIsSearchMode(state: IDialogStates, action: IDialogAction) {
    const { value } = action.payload;
    return produce(state, (draft) => {
        draft.isSearchMode = value;
    });
}

function checkIsCanSelectParentDepartment(isCanSelectParentDepartment: boolean, result: boolean) {
    if (isCanSelectParentDepartment) {
        return true;
    }

    return result;
}

function cleanSelectedList(state: IDialogStates, action: IDialogAction) {
    const { otherParams = {} } = action.payload;
    const { isCanSelectParentDepartment, selectableUseDepartmentIdsSet } = otherParams;

    return produce(state, (draft) => {
        draft.selectedList = [];
        draft.selectedMemberMap.clear();
        draft.selectedDepartmentMap.clear();

        draft.dataList.forEach((data) => {
            data.isSelected = false;
            data.isDisabledNextLevel = false;
            data.checkable =
                checkIsCanSelectParentDepartment(
                    isCanSelectParentDepartment,
                    selectableUseDepartmentIdsSet?.has?.(data.id),
                ) ?? true;
        });

        draft.departmentDataList.forEach((data) => {
            data.isSelected = false;
            data.isDisabledNextLevel = false;
            data.checkable =
                checkIsCanSelectParentDepartment(
                    isCanSelectParentDepartment,
                    selectableUseDepartmentIdsSet?.has?.(data.id),
                ) ?? true;
        });
    });
}

function setAllListChecked(state: IDialogStates, action: IDialogAction) {
    const { isChecked, otherParams } = action.payload;

    return produce(state, (draft) => {
        draft.dataList.forEach((data) => {
            const isDisabledCheck = onDisablePermissionSwitch(data.oriData);
            data.isSelected = isDisabledCheck ? false : isChecked;
            isChecked && !isDisabledCheck ? setSelectedItem(draft, data) : removeSelectedItem(draft, data);

            if (
                otherParams.isMutualExclusionOfNotInSeDepartment &&
                Number(data.id) === Number(otherParams.notInSeDepartmentValue)
            ) {
                data.checkable = !isChecked;
                if (isChecked) {
                    data.isSelected = false;
                    removeSelectedItem(draft, data);
                }
            }
        });
    });
}

function updateDataListToHandleNotInSeDepartment(state: IDialogStates, item: IListItem, action: IDialogAction) {
    const { otherParams = {} } = action.payload;

    if (
        state.selectedDepartmentMap.size > 0 &&
        item.id === otherParams.notInSeDepartmentValue &&
        !state.selectedDepartmentMap.has(otherParams.notInSeDepartmentValue)
    ) {
        item.checkable = false;
        item.isSelected = false;
    }

    if (
        item.id !== otherParams.notInSeDepartmentValue &&
        state.selectedDepartmentMap.has(otherParams.notInSeDepartmentValue)
    ) {
        item.checkable = false;
        item.isSelected = false;
        item.isDisabledNextLevel = true;
    }
}

function updateDataList(list: IListItem[], state: IDialogStates, action: IDialogAction) {
    const { otherParams = {} } = action.payload;
    const { isCanSelectParentDepartment, selectableUseDepartmentIdsSet } = otherParams;

    return produce(list, (draft) => {
        draft = draft.filter((data: IListItem) => {
            if (!state.excludeMap[data.id]) {
                return true;
            }
            return false;
        });
        draft.forEach((data: IListItem) => {
            let isExisted = false;

            if (data.type === TravelType.department) {
                isExisted = state.selectedDepartmentMap.has(data.id);
            } else {
                isExisted = state.selectedMemberMap.has(data.id);
            }

            if (data.isSelected || isExisted) {
                data.isSelected = true;
            }
            data.checkable =
                checkIsCanSelectParentDepartment(
                    isCanSelectParentDepartment,
                    selectableUseDepartmentIdsSet?.has?.(data.id),
                ) ?? true;

            if (data.type === TravelType.department && otherParams.isMutualExclusionOfNotInSeDepartment) {
                updateDataListToHandleNotInSeDepartment(state, data, action);
            }
        });
    });
}

function setSelectedList(state: IDialogStates, action: IDialogAction) {
    const list = action.payload.list as IListItem[];
    const departmentList = list.filter((item) => item.type === TravelType.department);
    const memberList = list.filter((item) => item.type === TravelType.owner);
    return produce(state, (draft) => {
        departmentList.forEach((item) => {
            draft.selectedDepartmentMap.set(item.id, item);
        });
        memberList.forEach((item) => {
            draft.selectedMemberMap.set(item.id, item);
        });
        updateSelectedList(draft);
    });
}

function setMemberSelectedMap(state: IDialogStates, action: IDialogAction) {
    const list = action.payload.list as IListItem[];
    return produce(state, (draft) => {
        list.forEach((item) => {
            draft.selectedMemberMap.set(item.id, item);
        });
        updateSelectedList(draft);
    });
}

function setDepartmentSelectedMap(state: IDialogStates, action: IDialogAction) {
    const list = action.payload.list as IListItem[];

    return produce(state, (draft) => {
        list.forEach((item) => {
            draft.selectedDepartmentMap.set(item.id, item);
        });
        updateSelectedList(draft);
    });
}

function setDataList(state: IDialogStates, action: IDialogAction) {
    const { currDepartmentId, pInfo, memberList = [], departmentList = [] } = action.payload;

    return produce(state, (draft) => {
        draft.pageInfo = {
            count: (pInfo && pInfo.count) || draft.pageInfo.count,
            currentPage: (pInfo && pInfo.currentPage) || draft.pageInfo.currentPage,
            totalPage: (pInfo && pInfo.totalPage) || draft.pageInfo.totalPage,
            size: (pInfo && pInfo.size) || draft.pageInfo.size,
        };

        draft.ts = Date.now();
        draft.isSearchMode = false;
        draft.departmentCache.currDepartmentId = currDepartmentId;
        draft.memberDataList = updateDataList(memberList, draft, action);
        draft.departmentDataList = updateDataList(departmentList, draft, action);

        draft.memberDataList.forEach((item) => {
            if (draft.selectedMemberMap.has(item.id)) {
                item.isSelected = true;
            }
        });

        draft.dataList = [...draft.departmentDataList, ...draft.memberDataList];
    });
}

function updateSugList(list: IListItem[], state: IDialogStates) {
    const sug = [...list];
    sug.forEach((data: any) => {
        let isExisted = false;
        if (data.type === TravelType.department) {
            isExisted = state.selectedDepartmentMap.has(data.id);
        } else {
            isExisted = state.selectedMemberMap.has(data.id);
        }
        data.isSelected = data.isSelected || isExisted;
    });
    return sug;
}

function setDataSug(state: IDialogStates, action: IDialogAction) {
    const { memberList, departmentList, isSearchMode } = action.payload;

    const newState = produce(state, (draft) => {
        draft.ts = Date.now();
        draft.isSearchMode = isSearchMode;
        draft.memberDataList = updateSugList(memberList, draft);
        draft.departmentDataList = updateSugList(departmentList, draft);
        draft.dataList = [...draft.departmentDataList, ...draft.memberDataList];
    });

    return newState;
}

/**
 * @description: 选中/取消选中 更新 dataList 与 selectedList
 * @param {IDialogStates} state
 * @param {IDialogAction} action
 */
function updateDataListAndSelectedList(state: IDialogStates, action: IDialogAction) {
    const { item, isAdd, otherParams = {} } = action.payload;
    const { isCanSelectParentDepartment, selectableUseDepartmentIdsSet } = otherParams;

    const newState = produce(state, (draft) => {
        draft.ts = Date.now();

        if (otherParams.isMutualExclusionOfNotInSeDepartment) {
            if (Number(item.id) === Number(otherParams.notInSeDepartmentValue)) {
                draft.dataList.forEach((data) => {
                    const mergedCheckable = !isAdd;
                    data.isDisabledNextLevel = !!isAdd;
                    data.checkable = mergedCheckable
                        ? checkIsCanSelectParentDepartment(
                              isCanSelectParentDepartment,
                              selectableUseDepartmentIdsSet?.has?.(data.id),
                          ) ?? true
                        : false;
                });
            } else {
                const notInSeDepartmentIndex = draft.dataList.findIndex(
                    (item) => item.id === otherParams.notInSeDepartmentValue,
                );
                if (notInSeDepartmentIndex !== -1 && draft.selectedDepartmentMap.size === 0) {
                    draft.dataList[notInSeDepartmentIndex].checkable = !isAdd;
                }
            }
        }

        const index = draft.dataList.findIndex((i) => {
            return i.id === item.id;
        });

        if (index !== -1 && isAdd != undefined) {
            draft.dataList[index].isSelected = !!isAdd;
            draft.dataList[index].isDisabledNextLevel = false;
            draft.dataList[index].checkable =
                checkIsCanSelectParentDepartment(
                    isCanSelectParentDepartment,
                    selectableUseDepartmentIdsSet?.has?.(draft.dataList[index].id),
                ) ?? true;
        }

        !!isAdd ? setSelectedItem(draft, item) : removeSelectedItem(draft, item);
    });

    return newState;
}

/**
 * @description: 筛选更新 selectedList
 */

function updateSelectedItem(state: IDialogStates, action: IDialogAction) {
    const { item } = action.payload;

    const newState = produce(state, (draft) => {
        setSelectedItem(draft, item);
    });

    return newState;
}

/**
 * @description: 新增或更新选中元素
 * @param {IDialogStates} state
 * @param {IListItem} item
 * 逻辑:
 *      部门: 添加到最后一个部门后面
 *      成员: 直接添加到后面
 */
function setSelectedItem(state: IDialogStates, item: IListItem) {
    if (item.type === TravelType.department) {
        state.selectedDepartmentMap.set(item.id, item);
    } else {
        state.selectedMemberMap.set(item.id, item);
    }

    updateSelectedList(state);
}

/**
 * @description: 删除选中元素
 * @param {IDialogStates} state
 * @param {IListItem} item
 */
function removeSelectedItem(state: IDialogStates, item: IListItem) {
    if (item.type === TravelType.department) {
        state.selectedDepartmentMap.delete(item.id);
    } else {
        state.selectedMemberMap.delete(item.id);
    }

    updateSelectedList(state);
}

function updateSelectedList(state: IDialogStates) {
    state.selectedList = [];
    state.selectedDepartmentMap.forEach((item) => {
        state.selectedList.push(item);
    });
    state.selectedMemberMap.forEach((item) => {
        state.selectedList.push(item);
    });
}
